import { useEffect, useState } from "react";
import { X, ShoppingBag } from "lucide-react";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const QuickView = ({ product, onClose }) => {
  const { addItem } = useCart();
  const [size, setSize] = useState(product?.sizes?.[0] || "One Size");
  if (!product) return null;

  const add = () => {
    addItem(product, size);
    toast.success("Added to cart", { description: `${product.name} — Size ${size}` });
    onClose();
  };

  return (
    <div data-testid="quickview-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface max-w-3xl w-full grid grid-cols-1 md:grid-cols-2 overflow-hidden max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <img src={product.image_url} alt={product.name} className="w-full h-64 md:h-full object-cover" />
        <div className="p-8 flex flex-col overflow-y-auto">
          <div className="flex items-start justify-between">
            <span className="overline-label text-signal">{product.category}</span>
            <button data-testid="quickview-close" onClick={onClose} className="text-muted-ink hover:text-signal"><X size={20} /></button>
          </div>
          <h2 className="font-display text-2xl font-bold text-ink mt-3 leading-snug">{product.name}</h2>
          <span className="font-mono2 text-xl text-ink mt-2">${product.price.toFixed(2)}</span>
          <p className="font-body text-muted-ink mt-4 leading-relaxed">{product.description}</p>

          <div className="mt-6">
            <div className="overline-label text-muted-ink mb-2">Select Size</div>
            <div className="flex flex-wrap gap-2">
              {product.sizes.map((s) => (
                <button
                  key={s}
                  data-testid={`size-${s}`}
                  onClick={() => setSize(s)}
                  className={`font-mono2 text-xs px-4 py-2 border transition-colors ${
                    size === s ? "bg-ink text-paper border-ink" : "border-rule text-ink hover:border-ink"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <button
            data-testid="quickview-add"
            onClick={add}
            className="mt-auto pt-6 w-full bg-signal text-white font-mono2 text-xs uppercase tracking-[0.2em] py-4 hover:opacity-90 flex items-center justify-center gap-2"
          >
            <ShoppingBag size={16} /> Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
};

const Store = () => {
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState(null);
  const { addItem } = useCart();

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data));
  }, []);

  const quickAdd = (e, p) => {
    e.stopPropagation();
    addItem(p, p.sizes?.[0] || "One Size");
    toast.success("Added to cart", { description: p.name });
  };

  return (
    <div className="bg-paper min-h-screen">
      <Header activeCategory={null} onCategory={() => {}} />

      {/* Store hero */}
      <section className="bg-masthead text-masthead-ink">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16 py-16">
          <div className="overline-label text-signal mb-3">// Field Supply Division</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight max-w-3xl">
            The Disclosure Store
          </h1>
          <p className="font-body text-lg text-masthead-ink/60 mt-4 max-w-xl">
            Heavyweight apparel and declassified-inspired goods for believers, researchers and the eternally curious.
          </p>
        </div>
      </section>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {products.map((p, idx) => (
            <article
              key={p.id}
              data-testid={`product-card-${p.id}`}
              onClick={() => setActive(p)}
              className="group cursor-pointer fade-up"
              style={{ animationDelay: `${idx * 60}ms` }}
            >
              <div className="relative overflow-hidden bg-surface border border-rule">
                <img src={p.image_url} alt={p.name} className="w-full h-72 object-cover transition-transform duration-500 group-hover:scale-105" />
                <button
                  data-testid={`quick-add-${p.id}`}
                  onClick={(e) => quickAdd(e, p)}
                  className="absolute bottom-0 left-0 right-0 bg-signal text-white font-mono2 text-[0.65rem] uppercase tracking-[0.2em] py-3 translate-y-full group-hover:translate-y-0 transition-transform"
                >
                  + Quick Add
                </button>
              </div>
              <div className="mt-4 flex items-start justify-between gap-3">
                <div>
                  <span className="overline-label text-muted-ink">{p.category}</span>
                  <h3 className="font-grotesk font-semibold text-sm text-ink mt-1 leading-snug">{p.name}</h3>
                </div>
                <span className="font-mono2 text-sm text-ink whitespace-nowrap">${p.price.toFixed(2)}</span>
              </div>
            </article>
          ))}
        </div>
      </main>

      <Footer />
      <QuickView key={active?.id} product={active} onClose={() => setActive(null)} />
    </div>
  );
};

export default Store;
